import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { setLoading } from '../../action/loading-actions'
import { fetchArtemisUsage } from '../../action/artemis-actions/query-action'
import { fetchArtemisCreditProducts, createArtemisCreditCheckout } from '../../action/artemis-actions/credit-actions'
import { FALLBACK_PACKS, checkoutUrls, packLabels, packsFrom, purchaseResult } from '../../lib/artemis/credits'
import { usageState } from '../../lib/artemis/usage'
import { tokenRates } from '../../lib/artemis/tokens'
import Icon from '../artemis-functionality/components/icons'
import './index.scss'

// After Stripe's return the grant arrives by webhook, so the balance is read again a few times.
const BALANCE_CHECKS = 5
const BALANCE_CHECK_MS = 3000

/**
 * /artemis/credits (WEB-7135): this month's Artemis use, credits left, and packs to buy through
 * Stripe Checkout (services API-3799), which returns here with ?purchase=success or cancelled.
 * Artemis's 429 BuyCreditsUrl points here (API-3800). Like /connected-apps it renders <Login /> in
 * place when signed out, and is excluded from the mobile app-store redirect (the apps open it in a
 * browser).
 */
export const ArtemisCreditsPage = ({ setLoading, fetchUsage, fetchProducts, createCheckout }) => {
  const [usage, setUsage] = useState(null)
  const [packs, setPacks] = useState(null)
  const [onSale, setOnSale] = useState(false)
  const [buying, setBuying] = useState(null)
  const [error, setError] = useState(null)
  const [result] = useState(() => purchaseResult(window.location.search))

  useEffect(() => {
    setLoading(false)
    fetchUsage().then(setUsage).catch(() => {})
    fetchProducts()
      .then(products => {
        const live = packsFrom(products)
        setPacks(live || FALLBACK_PACKS)
        setOnSale(!!live)
      })
      .catch(() => { setPacks(FALLBACK_PACKS); setOnSale(false) })
  }, [])

  // Back from a purchase: wait for the new grant to show up in the balance.
  useEffect(() => {
    if (result !== 'success') return undefined
    let checks = 0, stopped = false
    const start = usage ? Number(usage.CreditsUsd) || 0 : null
    const timer = setInterval(() => {
      if (stopped || ++checks > BALANCE_CHECKS) return clearInterval(timer)
      fetchUsage().then(next => {
        setUsage(next)
        if (start != null && (Number(next && next.CreditsUsd) || 0) > start) clearInterval(timer)
      }).catch(() => {})
    }, BALANCE_CHECK_MS)
    return () => { stopped = true; clearInterval(timer) }
  }, [result])

  const buy = pack => {
    if (!onSale || pack.id == null) return
    setBuying(pack.id); setError(null)
    createCheckout(pack.id, checkoutUrls(window.location.origin))
      .then(res => {
        if (res && (res.Url || res.url)) window.location.assign(res.Url || res.url)
        else throw new Error('no_url')
      })
      .catch(() => { setBuying(null); setError('Couldn’t start checkout. Try again in a moment.') })
  }

  const state = usageState(usage)
  const rates = tokenRates(usage)
  // Limits off (AllowanceUsd null), as in Production now: credits aren't spent, so they aren't sold.
  const unlimited = !!usage && usage.AllowanceUsd == null

  return (
    <div className='artemis-credits-bg'>
      <div className='artemis-credits-card'>
        <div className='artemis-credits-title'>
          <span className='artemis-credits-badge'><Icon name='sparkle' size={18} /></span>
          <h1>Artemis credits</h1>
        </div>

        {result === 'success' && (
          <p className='artemis-credits-banner artemis-credits-banner-ok' role='status'>
            Thanks! Your credits are on their way and will show here in a moment.
          </p>
        )}
        {result === 'cancelled' && (
          <p className='artemis-credits-banner' role='status'>Checkout was cancelled. You weren’t charged.</p>
        )}

        <section className='artemis-credits-usage' aria-label='This month'>
          <span className='artemis-credits-eyebrow'>This month</span>
          {!usage && <p className='artemis-credits-muted'>Loading your usage…</p>}
          {unlimited && <p className='artemis-credits-muted'>Artemis has no monthly limit on your account right now.</p>}
          {state.metered && (
            <>
              <div className='artemis-credits-meter-line'>
                <span>{state.limit ? 'This month’s allowance and credits are used' : state.onCredits ? 'Allowance used · Artemis is using your credits' : `${state.percent}% of this month’s Artemis used`}</span>
                {state.resets && <span className='artemis-credits-muted'>Resets {state.resets}</span>}
              </div>
              <span className={`artemis-credits-bar ${state.limit ? 'artemis-credits-bar-limit' : state.warn ? 'artemis-credits-bar-warn' : ''}`}
                role='meter' aria-label='Artemis used this month' aria-valuemin={0} aria-valuemax={100} aria-valuenow={state.percent}>
                <span style={{ width: `${state.percent}%` }} />
              </span>
              <p className='artemis-credits-balance'>
                {state.creditsLeft
                  ? <>Credits: <b>{state.creditsLeft}</b> left{state.creditsExpire ? `, next expiring ${state.creditsExpire}` : ''}</>
                  : 'No credits yet.'}
              </p>
              <p className='artemis-credits-muted'>Artemis uses your monthly allowance first, then your credits.</p>
            </>
          )}
        </section>

        <section className='artemis-credits-packs' aria-label='Credit packs'>
          <span className='artemis-credits-eyebrow'>Buy credits</span>
          <div className='artemis-credits-pack-list'>
            {(packs || FALLBACK_PACKS).map((pack, i) => {
              const label = packLabels(pack, rates)
              return (
                <div key={pack.id ?? i} className='artemis-credits-pack'>
                  <span className='artemis-credits-pack-price'>{label.price}</span>
                  <span className='artemis-credits-pack-credit'>{label.credit}</span>
                  {label.valid && <span className='artemis-credits-muted'>{label.valid}</span>}
                  <button type='button' className='artemis-credits-buy' disabled={!onSale || unlimited || buying != null} onClick={() => buy(pack)}>
                    {buying === pack.id && buying != null ? 'Opening checkout…' : `Buy ${label.price}`}
                  </button>
                </div>
              )
            })}
          </div>
          <p className='artemis-credits-muted'>
            Token counts are estimates{rates.own ? ', from how you’ve used Artemis this month' : ' for a typical mix of questions'}. Longer answers and bigger searches use more.
          </p>
          {packs && !onSale && <p className='artemis-credits-muted'>Credit packs aren’t on sale yet.</p>}
          {error && <p className='artemis-credits-error' role='alert'>{error}</p>}
          <p className='artemis-credits-fine'>Paid securely through Stripe. Credits expire a year after purchase.</p>
        </section>

        <button type='button' className='artemis-credits-back' onClick={() => { window.location = '/' }}>Back to BaseMap</button>
      </div>
    </div>
  )
}

const mapDispatchToProps = dispatch => ({
  setLoading: bool => dispatch(setLoading(bool)),
  fetchUsage: () => dispatch(fetchArtemisUsage()),
  fetchProducts: () => dispatch(fetchArtemisCreditProducts()),
  createCheckout: (productId, urls) => dispatch(createArtemisCreditCheckout(productId, urls))
})

export default connect(null, mapDispatchToProps)(ArtemisCreditsPage)
